import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { API_URL, message } from 'constant';
import { AllConditionalPipelinesState, SelectedPipelineState } from 'global-stores';
import { useNetwork, useNotification } from 'hooks';
import { validURL } from 'utils';
import { Drawer, DrawerHead, KV, Section } from 'views/design-system';

interface IConfigureDrawer {
	open: boolean;
	onClose: () => void;
}

/**
 * "Onboarding Flow Details", as a drawer.
 *
 * A straight port of the ViewPipeline modal: same fields, same required-field
 * gate on Save, same `validURL` check with its "Invalid Url" toast, the same
 * PATCH, and the same in-place recoil update afterwards — including rewriting
 * the publish actions' metadata, which is what keeps the builder's publish step
 * agreeing with the name and URLs shown here. Nothing about the request or the
 * state it writes changed; only where the form is drawn.
 */
export const ConfigureDrawer: FC<IConfigureDrawer> = ({ open, onClose }) => {
	const selectedPipeline = useRecoilValue(SelectedPipelineState);
	const [conditionalPipelines, setConditionalPipelines] = useRecoilState(
		AllConditionalPipelinesState
	);

	const { successNotification, errorNotification } = useNotification();
	const { patch: editOnboardingFlow, loading: editLoader } = useNetwork();

	const {
		COPY_TOASTER_MESSAGE_onboardingFlow,
		COPY_TOASTER_MESSAGE_creditional_Id,
		INVALID_URL_ADDRESS,
		UPDATE_SUCCESS_MESSAGE,
		ONBOARDING_FLOW_NAME_REQUIRED,
	} = useMemo(() => message, []);

	const {
		_id = '',
		name = '',
		redirectUrl = '',
		webhookUrl = '',
		credentialId = '',
		sessionCount = 0,
		slug: pipelineSlug = '',
		parentPipelineId: pipelineParentId = '',
	}: any = useMemo(() => selectedPipeline ?? {}, [selectedPipeline]);

	const [editValue, setEditValue] = useState({
		name,
		redirectUrl,
		webhookUrl,
		slug: pipelineSlug ?? '',
		parentPipelineId: pipelineParentId ?? '',
	});

	const change = useCallback(
		(field: string) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
			const { value } = e.target;
			setEditValue(prev => ({ ...prev, [field]: value }));
		},
		[]
	);

	const copy = useCallback(
		(value: string, toast: string) => {
			navigator.clipboard.writeText(value);
			successNotification(toast);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const canSave = useMemo(
		() =>
			[editValue.name, editValue.redirectUrl, editValue.webhookUrl].every(v =>
				String(v ?? '').trim()
			),
		[editValue]
	);

	const save = useCallback(() => {
		if (!validURL(editValue.redirectUrl) || !validURL(editValue.webhookUrl)) {
			errorNotification('Invalid Url');
			return;
		}
		if (selectedPipeline?.type !== 'complex') {
			errorNotification('Linear pipeline editing is currently disabled.');
			return;
		}

		editOnboardingFlow(`${API_URL.COMPLEX_FLOW}/${_id}`, {
			name: editValue.name.trim(),
			redirectUrl: editValue.redirectUrl,
			webhookUrl: editValue.webhookUrl,
			// Backend treats undefined as "untouched" and ''/null as "clear", so the
			// current value always goes up — clearing a slug is an edit too.
			slug: (editValue.slug ?? '').trim().toLowerCase(),
			parentPipelineId: editValue.parentPipelineId || null,
		}).then((res: any) => {
			if (res?._id) {
				setConditionalPipelines(prev => {
					const next = structuredClone(prev);
					const i = next.findIndex((el: any) => el._id === selectedPipeline._id);
					if (i !== -1) {
						const actions = next[i].configurations?.publish?.actions ?? [];
						const additional = actions.findIndex(
							(el: { actionId: string }) => el.actionId === 'publishAdditionalSetting'
						);
						const publish = actions.findIndex(
							(el: { actionId: string }) => el.actionId === 'publishOnboarding'
						);
						next[i].name = editValue.name?.trim();
						if (additional !== -1) {
							actions[additional].metadata = {
								name: editValue.name.trim(),
								publishRedirect: editValue.redirectUrl,
								publishWebhook: editValue.webhookUrl,
							};
						}
						if (publish !== -1) {
							actions[publish].metadata = {
								...actions[publish].metadata,
								publishName: editValue.name.trim(),
							};
						}
					}
					return next;
				});
				onClose();
				successNotification(UPDATE_SUCCESS_MESSAGE);
			}
			if (res?.message) errorNotification(res.message);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editValue, _id, selectedPipeline]);

	// The live redirect/webhook are not top-level fields — they live in the
	// publish step's metadata, which is what the builder writes.
	useEffect(() => {
		if (selectedPipeline?.type === 'complex') {
			const { publishRedirect, publishWebhook } =
				selectedPipeline.configurations?.publish?.actions?.find(
					(el: any) => el.actionId === 'publishAdditionalSetting'
				)?.metadata ?? {};
			setEditValue({
				name: selectedPipeline?.name ?? '',
				redirectUrl: publishRedirect ?? selectedPipeline?.redirectUrl ?? '',
				webhookUrl: publishWebhook ?? selectedPipeline?.webhookUrl ?? '',
				slug: selectedPipeline?.slug ?? '',
				parentPipelineId: selectedPipeline?.parentPipelineId ?? '',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPipeline, conditionalPipelines]);

	if (!selectedPipeline?._id) return <Drawer open={false} label="Onboarding flow details" />;

	return (
		<Drawer open={open} label="Onboarding flow details">
			<DrawerHead title={selectedPipeline?.name || 'Onboarding flow'} onClose={onClose}>
				<div className="dsteps">
					ID : {_id} &nbsp;&middot;&nbsp; {sessionCount || 0} session
					{Number(sessionCount) === 1 ? '' : 's'}
				</div>
				<div className="dbtns">
					<button className="dbtn" type="button" onClick={onClose}>
						Close
					</button>
					<button className="dbtn pri" type="button" disabled={!canSave || editLoader} onClick={save}>
						{editLoader ? 'Saving…' : 'Save'}
					</button>
				</div>
			</DrawerHead>

			<Section title="Identity">
				<KV
					rows={[
						[
							'Onboarding Flow Id',
							<>
								<span className="mono">{_id}</span>{' '}
								<button
									className="reinv"
									type="button"
									onClick={() => copy(_id, COPY_TOASTER_MESSAGE_onboardingFlow)}
								>
									Copy
								</button>
							</>,
						],
						[
							'Credential Id',
							credentialId ? (
								<>
									<span className="mono">{credentialId}</span>{' '}
									<button
										className="reinv"
										type="button"
										onClick={() => copy(credentialId, COPY_TOASTER_MESSAGE_creditional_Id)}
									>
										Copy
									</button>
								</>
							) : (
								<span className="gy">--</span>
							),
						],
						['Sessions', String(sessionCount ?? 0)],
					]}
				/>
			</Section>

			<Section title="Settings">
				<div className={`dfield${editValue.name?.trim() ? '' : ' err'}`}>
					<span className="dflabel">Onboarding Flow Name</span>
					<input value={editValue.name} onChange={change('name')} />
					{!editValue.name?.trim() && (
						<div className="dferr">{ONBOARDING_FLOW_NAME_REQUIRED}</div>
					)}
				</div>

				<div className={`dfield${editValue.redirectUrl?.trim() ? '' : ' err'}`}>
					<span className="dflabel">Redirect URL</span>
					<input value={editValue.redirectUrl} onChange={change('redirectUrl')} />
					{!editValue.redirectUrl?.trim() && (
						<div className="dferr">{INVALID_URL_ADDRESS}</div>
					)}
				</div>

				<div className={`dfield${editValue.webhookUrl?.trim() ? '' : ' err'}`}>
					<span className="dflabel">Webhook URL</span>
					<input value={editValue.webhookUrl} onChange={change('webhookUrl')} />
					{!editValue.webhookUrl?.trim() && (
						<div className="dferr">{INVALID_URL_ADDRESS}</div>
					)}
				</div>

				<div className="dfield">
					<span className="dflabel">Pipeline Unique Name</span>
					<input
						value={editValue.slug}
						placeholder="e.g. kyc-core (lowercase letters/numbers/hyphens)"
						onChange={change('slug')}
					/>
					<div className="dfhint">Optional. Unique per business.</div>
				</div>

				<div className="dfield">
					<span className="dflabel">Parent Pipeline</span>
					<select value={editValue.parentPipelineId} onChange={change('parentPipelineId')}>
						<option value="">None (standalone pipeline)</option>
						{/* Options come from the complex-flow list; AllPipelinesState is
						    unpopulated (its PIPELINE_USERS source is disabled), so reading
						    options from it left a persisted parent with no matching option. */}
						{(conditionalPipelines ?? [])
							.filter((p: any) => p?._id && p._id !== _id)
							.map((p: any) => (
								<option key={p._id} value={p._id}>
									{p.name}
								</option>
							))}
					</select>
				</div>
			</Section>
		</Drawer>
	);
};
